/**
 * DemoSwitcher — amber "Demo" pill button that opens a dropdown listing all
 * 5 Cascade Credit Union spotlight personas.
 *
 * Demo runners can jump instantly to any Cascade persona profile from anywhere
 * in the app (DEMO-03). Clicking a persona navigates to /members/:uuid using
 * the UUID from CASCADE_SPOTLIGHT_IDS.
 *
 * Behaviour:
 *   - Amber pill button in the header; shows "Demo" label + chevron
 *   - Clicking opens dropdown listing the 5 Cascade personas
 *   - Clicking a persona navigates to /members/:uuid and closes the dropdown
 *   - Clicking outside closes the dropdown independently
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, User } from 'lucide-react';
import { CASCADE_SPOTLIGHT_IDS } from '../pages/member360/mockProfiles';

const PERSONAS = [
  { name: 'Sarah Chen',          id: CASCADE_SPOTLIGHT_IDS['sarah-chen'] },
  { name: 'Marcus Rivera-Lopez', id: CASCADE_SPOTLIGHT_IDS['marcus-rivera-lopez'] },
  { name: 'Emily Nakamura',      id: CASCADE_SPOTLIGHT_IDS['emily-nakamura'] },
  { name: 'Robert Kowalski',     id: CASCADE_SPOTLIGHT_IDS['robert-kowalski'] },
  { name: 'Amara Osei',          id: CASCADE_SPOTLIGHT_IDS['amara-osei'] },
] as const;

export function DemoSwitcher(): React.ReactElement {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function selectPersona(memberId: string): void {
    setIsOpen(false);
    navigate('/members/' + memberId);
  }

  return (
    <div className="relative" ref={panelRef}>
      {/* Trigger button — amber pill */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Switch demo persona"
        aria-expanded={isOpen}
        className="px-3 py-1.5 rounded-full text-sm font-medium bg-warn-soft text-warn hover:bg-warn-soft transition-colors flex items-center gap-1.5"
      >
        Demo
        <ChevronDown className="h-3.5 w-3.5" />
      </button>

      {/* Persona dropdown */}
      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-56 rounded-lg border border-line bg-white shadow-lg z-50"
          role="menu"
        >
          {/* Section header */}
          <div className="px-4 py-2.5 border-b border-sunken text-xs font-semibold text-ink-muted uppercase tracking-wide">
            Cascade Personas
          </div>

          {/* Persona rows */}
          {PERSONAS.map((p) => (
            <button
              key={p.id}
              role="menuitem"
              onClick={() => selectPersona(p.id)}
              className="w-full px-4 py-2.5 text-sm text-ink hover:bg-bg text-left flex items-center gap-2"
            >
              <User className="h-4 w-4 text-ink-muted flex-shrink-0" />
              <span>{p.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
